import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, ExternalLink, Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import { getBiography, adminUpdateBiography } from "@/lib/api";
import { ImageUpload } from "@/admin/FormFields";

const inp = "w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none text-sm";

export default function BiographyEditor() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["biography"], queryFn: getBiography });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data) setForm(data); }, [data, setForm]);

  const mut = useMutation({
    mutationFn: (d) => adminUpdateBiography(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["biography"] }); toast.success("Biography saved"); },
    onError: () => toast.error("Save failed"),
  });

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (i, patch) => set("sections", form.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const moveSection = (i, dir) => {
    const arr = [...form.sections];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set("sections", arr);
  };
  const setInfobox = (i, patch) => set("infobox", form.infobox.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div data-testid="biography-editor">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">Biography</h1>
          <a href="/biography" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-civic font-medium mt-1">
            View public biography <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <button onClick={() => mut.mutate(form)} disabled={mut.isPending} data-testid="save-biography"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-60">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </button>
      </div>

      <div className="space-y-6">
        <Card title="Header">
          <Row label="Title"><input value={form.title || ""} onChange={(e) => set("title", e.target.value)} className={inp} data-testid="bio-field-title" /></Row>
          <Row label="Subtitle (one-line description)"><input value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={inp} data-testid="bio-field-subtitle" /></Row>
          <Row label="Photo"><ImageUpload value={form.photo} onChange={(v) => set("photo", v)} testid="bio-field-photo" /></Row>
          <Row label="Photo caption"><input value={form.photoCaption || ""} onChange={(e) => set("photoCaption", e.target.value)} className={inp} /></Row>
        </Card>

        <Card title="Infobox (right side facts table)">
          {(form.infobox || []).map((row, i) => (
            <div key={i} className="flex gap-2 items-start">
              <input value={row.label} onChange={(e) => setInfobox(i, { label: e.target.value })} placeholder="Label" className={`${inp} w-1/3`} data-testid={`infobox-label-${i}`} />
              <input value={row.value} onChange={(e) => setInfobox(i, { value: e.target.value })} placeholder="Value" className={inp} data-testid={`infobox-value-${i}`} />
              <button type="button" onClick={() => set("infobox", form.infobox.filter((_, idx) => idx !== i))} data-testid={`remove-infobox-row-${i}`}
                className="h-10 w-10 shrink-0 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => set("infobox", [...(form.infobox || []), { label: "", value: "" }])}
            className="inline-flex items-center gap-1.5 text-sm text-navy font-medium" data-testid="add-infobox-row">
            <Plus className="h-4 w-4" /> Add fact row
          </button>
        </Card>

        {(form.sections || []).map((s, i) => (
          <Card key={i} title={`Section ${i + 1}`}
            actions={
              <div className="flex items-center gap-1">
                <IconBtn onClick={() => moveSection(i, -1)} disabled={i === 0} data-testid={`bio-section-move-up-${i}`}><ArrowUp className="h-4 w-4" /></IconBtn>
                <IconBtn onClick={() => moveSection(i, 1)} disabled={i === form.sections.length - 1} data-testid={`bio-section-move-down-${i}`}><ArrowDown className="h-4 w-4" /></IconBtn>
                <button type="button" onClick={() => set("sections", form.sections.filter((_, idx) => idx !== i))} data-testid={`remove-bio-section-${i}`}
                  className="h-8 w-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center ml-1"><X className="h-4 w-4" /></button>
              </div>
            }>
            <Row label="Heading"><input value={s.heading || ""} onChange={(e) => setSection(i, { heading: e.target.value })} className={inp} data-testid={`bio-section-heading-${i}`} /></Row>
            <Row label="Content (separate paragraphs with a blank line)">
              <textarea rows={5} value={s.content || ""} onChange={(e) => setSection(i, { content: e.target.value })} className={`${inp} resize-y`} data-testid={`bio-section-content-${i}`} />
            </Row>
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-500 font-medium">Kannada version (optional)</summary>
              <div className="space-y-3 mt-3">
                <Row label="Heading (Kannada)"><input value={s.heading_kn || ""} onChange={(e) => setSection(i, { heading_kn: e.target.value })} className={inp} /></Row>
                <Row label="Content (Kannada)"><textarea rows={4} value={s.content_kn || ""} onChange={(e) => setSection(i, { content_kn: e.target.value })} className={`${inp} resize-y`} /></Row>
              </div>
            </details>
          </Card>
        ))}

        <button type="button" data-testid="add-bio-section"
          onClick={() => set("sections", [...(form.sections || []), { heading: "", content: "" }])}
          className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-300 text-slate-500 font-semibold hover:border-gold hover:text-gold transition-colors">
          <Plus className="h-4 w-4 inline mr-1" /> Add Section
        </button>
      </div>
    </div>
  );
}

function Card({ title, children, actions }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-lg font-semibold text-navy">{title}</h2>
        {actions}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-navy mb-1.5">{label}</label>
      {children}
    </div>
  );
}
function IconBtn({ children, ...props }) {
  return (
    <button type="button" {...props}
      className="h-8 w-8 rounded-lg bg-slate-100 text-navy flex items-center justify-center disabled:opacity-30">
      {children}
    </button>
  );
}
